import { mergeClasses } from "@fluentui/react-components";
import { DataPieRegular } from "@fluentui/react-icons";
import { ProjectStatus } from "apps/decooda/components/projects/projectConstants";
import { useState } from "react";
import { convertDateStringToUnix, formatDate } from "utils/generalUtils";
import { useAnalyticsCardStyles } from "./AnalyticsCard.styles";
import { IAnalyticsCardProps } from "./AnalyticsCard.types";
import { AnalyticsCardMenu } from "./AnalyticsCardMenu";

const STATUS_LABELS: Record<ProjectStatus, string> = {
  [ProjectStatus.NotScored]: "Not Scored",
  [ProjectStatus.InProgress]: "In Progress",
  [ProjectStatus.Scored]: "Scored",
  [ProjectStatus.Error]: "Failed",
};

export const AnalyticsCard = ({
  project,
  onNavigate,
  onEdit,
  onDelete,
  onScore,
  onExport,
  isExporting = false,
}: IAnalyticsCardProps): JSX.Element => {
  const styles = useAnalyticsCardStyles();
  const [isHovered, setIsHovered] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const status = project.status ?? ProjectStatus.NotScored;
  const attachmentCount = project.attachments?.length ?? 0;
  const attachmentLabel = `${attachmentCount} attachment${attachmentCount !== 1 ? "s" : ""}`;
  const formattedDate = formatDate(
    convertDateStringToUnix(project.modifiedAt ?? project.created),
  );

  const statusPillClass = mergeClasses(
    styles.analyticsCardStatusPill,
    status === ProjectStatus.InProgress
      ? styles.analyticsCardStatusInProgress
      : status === ProjectStatus.Scored
        ? styles.analyticsCardStatusCompleted
        : status === ProjectStatus.Error
          ? styles.analyticsCardStatusError
          : styles.analyticsCardStatusDraft,
  );

  return (
    <div
      className={styles.analyticsCard}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <button
        className={styles.analyticsCardButton}
        aria-label={project.name}
        onClick={onNavigate}
      >
        <div className={styles.analyticsIcon} aria-hidden="true">
          <DataPieRegular />
        </div>
        <div className={styles.analyticsCardBody}>
          <div className={styles.analyticsCardNameRow}>
            <span className={styles.analyticsCardName}>{project.name}</span>
            <span className={statusPillClass}>{STATUS_LABELS[status]}</span>
          </div>
          <p
            className={
              project.objective
                ? styles.analyticsCardDescription
                : styles.analyticsCardDescriptionEmpty
            }
            title={project.objective || undefined}
          >
            {project.objective || "No description"}
          </p>
          <div className={styles.analyticsCardMeta}>
            <span className={styles.analyticsCardMetaAttachments}>
              {attachmentLabel}
            </span>
            <span aria-hidden="true">&bull;</span>
            <span>{formattedDate}</span>
          </div>
        </div>
      </button>
      <div
        className={mergeClasses(
          styles.actionArea,
          (isHovered || isMenuOpen) && styles.actionAreaVisible,
        )}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <AnalyticsCardMenu
          onEdit={onEdit}
          onDelete={onDelete}
          onScore={onScore}
          onExport={onExport}
          isExporting={isExporting}
          status={status}
          onOpenChange={setIsMenuOpen}
        />
      </div>
    </div>
  );
};
